import { useMemo } from "react";
import {
  getDailyExpenseCategories,
  getDailyExpenseDay,
  getDailyExpenseMonth,
  getDailyExpenseReview,
} from "../../services/dailyExpense";
import {
  DailyExpenseDay,
  DailyExpenseMonth,
  DailyExpenseReview,
  DailyExpenseReviewParams,
} from "../../types/daily-expense.type";
import { CategoryLayout, layoutOf } from "./dailyExpenseForm";
import { useLatestAnswer } from "./useLatestAnswer";

/** The day a screen reads: head office names the branch, a branch leaves it out. */
export interface DayKey {
  businessDate: string;
  branchCode?: string;
}

/** The category list is the same for every caller and every day, so it is asked once. */
const CATEGORY_REQUEST = "DAILY";

const loadCategoryLayout = async (): Promise<CategoryLayout> => {
  const { data } = await getDailyExpenseCategories();
  return layoutOf(data.data?.rows ?? []);
};

const loadMonth = async (month: string): Promise<DailyExpenseMonth | null> => {
  const { data } = await getDailyExpenseMonth(month);
  return data.data;
};

const loadDay = async (key: DayKey): Promise<DailyExpenseDay | null> => {
  const { data } = await getDailyExpenseDay(key.businessDate, key.branchCode);
  return data.data;
};

const loadReview = async (
  params: DailyExpenseReviewParams,
): Promise<DailyExpenseReview | null> => {
  const { data } = await getDailyExpenseReview(params);
  return data.data;
};

/** Where every daily category sits on the form (ฝ่ายขาย columns, ฝ่ายบริหาร fields, อื่นๆ). */
export const useCategoryLayout = () => useLatestAnswer(CATEGORY_REQUEST, loadCategoryLayout);

/** S2 list: the branch's month. `month` is `YYYY-MM`, or null to ask nothing. */
export const useDailyExpenseMonth = (month: string | null) => useLatestAnswer(month, loadMonth);

/** One day's document, routes and history. `key` must keep its identity between renders. */
export const useDailyExpenseDay = (key: DayKey | null) => useLatestAnswer(key, loadDay);

/** S3 list and branch × day view. `params` must keep its identity between renders. */
export const useDailyExpenseReview = (params: DailyExpenseReviewParams | null) =>
  useLatestAnswer(params, loadReview);

/** The day key for a date (and branch), stable while neither changes. */
export const useDayKey = (businessDate: string | null, branchCode?: string): DayKey | null =>
  useMemo(
    () => (businessDate ? { businessDate, branchCode } : null),
    [businessDate, branchCode],
  );
